import { navigateBackOrFallback } from "../../shared/screen-shell/screen-navigation.utils";
import {Component, OnInit} from "@angular/core";
import {PlayerService} from "../../shared/services/player.service";
import {BehaviorSubject} from "rxjs";
import {Player} from "../../shared/models/player";
import {ActivatedRoute, Router} from "@angular/router";
import {Chat, ChatEntityType} from "../../shared/models/chat";
import {ChatService} from "../../shared/services/chat.service";
import {Message} from "../../shared/models/message";
import {EventService} from "../../shared/services/event.service";
import {Location} from '@angular/common';
import { DelayedSpinnerService } from "../../shared/services/delayed-spinner.service";

@Component({
    selector: 'chat-detail',
    templateUrl: './chat-detail.component.html',
    styleUrls: ['../chat.component.css'],
    providers: [],
    standalone: false
})
export class ChatDetailComponent implements OnInit {

  public chat: Chat;

  public loading = false;
  public readonly chatDetailReady$ = new BehaviorSubject<boolean>(false);
  public errorMsg: string;

  public playerFilter: string = "";
  public playerCandidates: Player[] = [];
  public visiblePlayerCandidates: Player[] = [];
  public loadingPlayers: boolean = false;

  public showDeleteDialog: boolean = false;

  private chatOld: Chat;
  private currentPlayer: Player;

  private chatEntityType = new ChatEntityType();

  constructor(public playerService: PlayerService,
              public chatService: ChatService,
              public route: ActivatedRoute,
              public eventService: EventService,
              public router: Router,
              private _location: Location,
              private spinner: DelayedSpinnerService) {
  }

  ngOnInit() {
    window.scroll(0, 0);

    let me = this;
    me.loading = true;
    this.chatDetailReady$.next(false);

    me.currentPlayer = me.playerService.getCurrentPlayer();

    let newChat = me.route.snapshot.params['newChat'];
    if (newChat) {
      me.chat = me.createNewChat();
      me.loadPlayerCandidates(true);
      return;
    } else if (!localStorage.getItem('chat')) {
      me.router.navigate(['/chat-list']);

      return;
    } else {
      me.chat = JSON.parse(localStorage.getItem('chat'));
      me.chatOld = JSON.parse(JSON.stringify(me.chat));

      let superAdmin = this.currentPlayer.superAdmin;
      if (me.currentPlayer.id === me.chat.contact.id || superAdmin) {
        me.chat.viewedByManager = true;
      } else {
        me.chat.viewedByManager = false;
        this.finishChatDetailLoading();

        return;
      }

      this.chatService.getPlayers(this.chat.id).subscribe(players => {
        this.chat.players = players;
        this.loadPlayerCandidates(true);
      }, error => {
        console.log(error);
        this.errorMsg = error.error;

        this.chat.players = Array<Player>();
        this.loadPlayerCandidates(true);
      });

      this.changeDisplayName();
      return;
    }
  }

  private changeDisplayName() : void{
    this.chatService.calculateDisplayName(this.chat, this.currentPlayer.id);
  }

  public chatSaved = false;
  public chatSaving = false;

  public saveChat(): void {
    if (this.chatSaving || !this.canSaveChat()) {
      return;
    }

    this.chatSaving = true;
    this.chatSaved = false;
    if (this.chat.id) {
      this.chat.name = this.chat.displayName;
      this.chatService.updateChat(this.chat).subscribe(savedChat => {
        this.chat = savedChat;
        this.chat.viewedByManager = true;

        this.applySavedChat(savedChat);
      }, error => {
        this.chatSaving = false;
        console.log(error);
        this.errorMsg = error.error;
      });
    } else if (this.chat.players.length === 1) {
      this.chatService.openDirectChat(this.chat.players[0].id).subscribe(thread => {
        const savedChat = thread && thread.chat ? thread.chat : null;
        this.applySavedChat(savedChat);
      }, error => {
        this.chatSaving = false;
        console.log(error);
        this.errorMsg = error.error;
      });
    } else {
      this.chatService.createChat(this.chat).subscribe(savedChat => {
        this.chat = savedChat;
        this.chat.viewedByManager = true;

        this.applySavedChat(savedChat);
      }, error => {
        this.chatSaving = false;
        console.log(error);
        this.errorMsg = error.error;
      });
    }
  }

  public cancelChatDetailsChange(): void {
    if (this.chat.id) {
      this.chat = JSON.parse(JSON.stringify(this.chatOld));
      this.router.navigate(['/messages', {chatId: this.chat.id}]);
    } else {
      this.router.navigate(['/chat-list']);
    }
  }

  public canSaveChat(): boolean {
    return !!(this.chat && this.chat.players && this.chat.players.length > 0);
  }

  private applySavedChat(savedChat: Chat): void {
    this.chatSaving = false;
    if (!savedChat?.id) return;
    this.chatSaved = true;
    this.chat = savedChat;
    this.chat.displayName = savedChat.displayName || savedChat.name;
    this.chatOld = JSON.parse(JSON.stringify(this.chat));
    this.errorMsg = null;
  }

  public addPlayerToChat(newPlayer: Player) {
    let currentPlayersOnChat = this.chat.players;
    let playerFound: boolean = false;

    for (let player of currentPlayersOnChat) {
      if (player.id === newPlayer.id) {
        playerFound = true;
        break;
      }
    }

    if (!playerFound) {
      this.chat.players.push(newPlayer);
      this.updateVisiblePlayerCandidates();
    }
  }

  public removePlayerFromChat(player: Player) {
    let index = 0;
    for (let pl of this.chat.players) {
      if (player.id === pl.id) {
        this.chat.players.splice(index, 1);
        this.updateVisiblePlayerCandidates();
        break;
      }

      index++;
    }
  }

  public filteredPlayers(): Player[] {
    const filter = (this.playerFilter || "").trim().toLowerCase();
    return (this.playerCandidates || [])
      .filter(player => !this.isPlayerOnChat(player))
      .filter(player => {
        if (!filter) {
          return true;
        }
        return [
          player.nickName,
          player.name,
          player.autocompleteName,
          player.email
        ].some(value => value && value.toLowerCase().indexOf(filter) >= 0);
      });
  }

  public onPlayerFilterChange(): void {
    this.updateVisiblePlayerCandidates();
  }

  public getPlayerDisplayName(player: Player): string {
    return player && (player.nickName || player.name || player.email) ? (player.nickName || player.name || player.email) : "";
  }

  public getPlayerInitials(player: Player): string {
    const value = this.getPlayerDisplayName(player);
    const parts = value.trim().split(/\s+/).filter(part => !!part);
    if (parts.length === 0) {
      return "?";
    }
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  public removeChat(): void {
    this.spinner.show();
    this.loading = true;

    this.chatService.removeChat(this.chat.id).subscribe(() => {
      this.router.navigate(['/chat-list']);
      this.spinner.hide();
      this.loading = false;
    }, res => {
      this.router.navigate(['/chat-list']);
      console.log(res.error);
      this.spinner.hide();
      this.loading = false;
    });
  }

  private createNewChat(): Chat {
    let chat = new Chat();
    chat.contact = this.currentPlayer;
    chat.name = this.currentPlayer.name + ' - chat';
    chat.entityId = this.currentPlayer.id;
    chat.entityType = this.chatEntityType.PLAYER;
    chat.entityName = this.currentPlayer.name;
    chat.mutedPlayers = Array<string>();
    chat.text = '';
    chat.viewedByManager = true;
    chat.messages = Array<Message>();
    chat.players = Array<Player>();

    return chat;
  }

  private loadPlayerCandidates(showScreenWhenDone: boolean = false): void {
    const search = (this.playerFilter || "").trim();
    this.loadingPlayers = true;
    this.playerService.getFootballerFriends(search).subscribe(players => {
      this.playerCandidates = (players || [])
        .filter(player => player && player.id && (!this.currentPlayer || player.id !== this.currentPlayer.id))
        .sort((a, b) => this.getPlayerDisplayName(a).localeCompare(this.getPlayerDisplayName(b)));
      this.updateVisiblePlayerCandidates();
      this.loadingPlayers = false;
      if (showScreenWhenDone) {
        this.finishChatDetailLoading();
      }
    }, error => {
      console.log(error);
      this.playerCandidates = [];
      this.updateVisiblePlayerCandidates();
      this.loadingPlayers = false;
      console.error("[chat-detail] footballer friends failed", {
        search,
        status: error && error.status,
        message: error && error.message
      });
      if (showScreenWhenDone) {
        this.finishChatDetailLoading();
      }
    });
  }

  private updateVisiblePlayerCandidates(): void {
    this.visiblePlayerCandidates = this.filteredPlayers();
  }

  private finishChatDetailLoading(): void {
    this.loading = false;
    this.chatDetailReady$.next(true);
  }

  private isPlayerOnChat(player: Player): boolean {
    return !!(player && this.chat && this.chat.players && this.chat.players.some(existing => existing && existing.id === player.id));
  }

  public goBack():void{
    //console.log('uh');
    navigateBackOrFallback(this._location, this.router, ["/chat-list"]);
  }
}
